import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload } from 'lucide-react';
import { usePageEditMode } from '@/context/PageEditModeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Inline-editable podcast cover image.
 * Shows a "Replace cover" button in edit mode. Picks a file, POSTs
 * /api/podcasts/{id}/cover and updates the local state.
 *
 * Props:
 *   podcast        current podcast object (needs id)
 *   children       the current image element to render underneath the overlay
 *   onSaved        (updatedPodcast) => void
 */
export const InlinePodcastImage = ({
  podcast,
  className = '',
  onSaved,
  children,
}) => {
  const { enabled } = usePageEditMode();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    inputRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('cover_image', file);
      const res = await axios.post(`${API}/podcasts/${podcast.id}/cover`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      const newUrl = res.data?.cover_url;
      if (!newUrl) throw new Error('Upload returned no URL');
      toast.success('Cover updated');
      if (onSaved) onSaved({ ...podcast, cover_url: newUrl });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {children}
      {enabled && (
        <>
          <button
            onClick={handlePick}
            disabled={uploading}
            data-edit-path={`podcast.${podcast?.id}.cover`}
            className="absolute inset-0 flex items-center justify-center gap-2 bg-black/70 text-white rounded-xl hover:bg-black/85 transition-colors z-[5] cursor-pointer disabled:cursor-wait"
            data-testid={`podcast-cover-replace-${podcast?.id}`}
          >
            <Upload className="w-5 h-5" />
            <span className="text-xs font-semibold">
              {uploading ? 'Uploading…' : 'Replace cover'}
            </span>
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            className="hidden"
          />
        </>
      )}
    </div>
  );
};

export default InlinePodcastImage;
